import { Link } from 'react-router-dom'
import { Button, Chip, EmptyState, Skeleton, Table, TBody, TD, TH, THead, TR } from '../components/ui'
import { PageHeader } from '../components/PageHeader'
import { useSystems, useUnknownCredentials } from '../api/hooks'
import type { UnknownCredential } from '../api/types'
import { relativeTime, shortDateTime } from '../lib/format'

function credKind(c: UnknownCredential): string {
  return c.code_raw && c.code_raw.includes(':') ? 'Tag' : 'Code'
}

export default function UnknownCredentialsPage() {
  const unknownCreds = useUnknownCredentials('open')
  const systems = useSystems()

  const systemNames = new Map((systems.data ?? []).map((s) => [s.unique_id, s.name]))

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Directory" title="Pending credentials" />

      <p className="text-sm text-ostrel-ink-muted">
        Unknown credentials seen at connected systems. Review each one in the source system’s mapping
        workbench to convert it into a holder or attach it to an existing credential.
      </p>

      {unknownCreds.isPending || systems.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : unknownCreds.isError ? (
        <EmptyState title="Could not load pending credentials" hint={unknownCreds.error.message} />
      ) : systems.isError ? (
        <EmptyState title="Could not load systems" hint={systems.error.message} />
      ) : (unknownCreds.data ?? []).length === 0 ? (
        <EmptyState
          title="No pending credentials"
          hint="Unknown credentials that appear on any connected system will land here."
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Code</TH>
              <TH>Kind</TH>
              <TH>Systems</TH>
              <TH>Last seen</TH>
              <TH>Seen count</TH>
              <TH className="text-right">Action</TH>
            </tr>
          </THead>
          <TBody>
            {(unknownCreds.data ?? []).map((c) => {
              const openSighting = c.sightings.find((s) => s.status === 'open') ?? c.sightings[0]
              const systemUids = [...new Set(c.sightings.map((s) => s.system))]
              return (
                <TR key={c.code}>
                  <TD>
                    <code className="tnum rounded-sm border border-ostrel-line bg-ostrel-sand px-1.5 py-0.5 font-mono text-sm text-ostrel-ink">
                      {c.code}
                    </code>
                    {c.code_raw && c.code_raw !== c.code && (
                      <span className="tnum ml-2 block text-[10px] text-ostrel-ink-muted">
                        raw {c.code_raw}
                      </span>
                    )}
                  </TD>
                  <TD>
                    <Chip tone="muted" label={credKind(c)} />
                  </TD>
                  <TD>
                    <div className="flex flex-col gap-0.5">
                      {systemUids.map((uid) => (
                        <span key={uid} className="text-xs text-ostrel-ink">
                          {systemNames.get(uid) ?? uid}
                        </span>
                      ))}
                    </div>
                  </TD>
                  <TD>
                    <span className="tnum block text-xs text-ostrel-ink" title={c.last_seen_at}>
                      {shortDateTime(c.last_seen_at)}
                    </span>
                    <span className="tnum block text-[10px] text-ostrel-ink-muted">
                      {relativeTime(c.last_seen_at)}
                    </span>
                  </TD>
                  <TD>
                    <span className="tnum text-sm text-ostrel-ink">{c.seen_count}</span>
                  </TD>
                  <TD className="text-right">
                    <Link to={`/systems/${openSighting.system}/mapping`}>
                      <Button variant="ghost" size="sm">
                        Open workbench
                      </Button>
                    </Link>
                  </TD>
                </TR>
              )
            })}
          </TBody>
        </Table>
      )}
    </div>
  )
}
